import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Tabs,
  Button,
  Space,
  Table,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  TimePicker,
  message,
  Badge,
  Progress,
  Calendar,
  Avatar,
  Tooltip,
  Radio,
  InputNumber,
  Switch,
  Alert
} from 'antd';
import {
  ClockCircleOutlined,
  CalendarOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FilterOutlined,
  SearchOutlined,
  DesktopOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { attendanceService, shiftService, employeeService, Employee } from '../../../../services/hrService';
import ZKDeviceManagement from './ZKDeviceManagement';

const { Title, Text } = Typography;
dayjs.extend(utc);

const { Option } = Select;
const { RangePicker } = DatePicker;

interface AttendanceRecord {
  id: string;
  employeeId: string;
  employee: {
    firstName: string;
    lastName: string;
    employeeId: string;
    department: string;
  };
  date: string;
  checkIn?: string;
  checkOut?: string;
  breakTime: number; // in minutes
  hoursWorked: number;
  overtime: number;
  status: 'present' | 'absent' | 'late' | 'half-day' | 'leave';
  notes?: string;
  location: 'office' | 'remote' | 'field';
  isManualEntry: boolean;
  approvedBy?: string;
}

interface Shift {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  breakDuration: number; // in minutes
  isActive: boolean;
  departments: string[];
}

interface AttendancePolicy {
  id: string;
  name: string;
  workingHours: number;
  lateGracePeriod: number; // in minutes
  halfDayThreshold: number; // in hours
  overtimeThreshold: number; // in hours
  isActive: boolean;
}

const AttendanceManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState('attendance');
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [policies, setPolicies] = useState<AttendancePolicy[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [isAttendanceModalVisible, setIsAttendanceModalVisible] = useState(false);
  const [isShiftModalVisible, setIsShiftModalVisible] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState<AttendanceRecord | null>(null);
  const [editingShift, setEditingShift] = useState<Shift | null>(null);
  const [currentTime, setCurrentTime] = useState(dayjs());
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [filters, setFilters] = useState<{
    employee: string;
    department: string;
    status: string;
    dateRange: [Dayjs, Dayjs] | null;
  }>({
    employee: '',
    department: '',
    status: '',
    dateRange: null
  });

  /**
   * The three controls above the table used to be decorative: the search box
   * and status dropdown wrote to state nobody read, the date picker had no
   * value or onChange at all, and the table was handed the unfiltered list.
   * Searching one name for one date returned everybody for every date.
   */
  const filteredAttendanceRecords = useMemo(() => {
    const term = filters.employee.trim().toLowerCase();
    const from = filters.dateRange?.[0]?.format('YYYY-MM-DD');
    const to = filters.dateRange?.[1]?.format('YYYY-MM-DD');

    return attendanceRecords.filter(record => {
      if (term) {
        const name = `${record.employee?.firstName || ''} ${record.employee?.lastName || ''}`.toLowerCase();
        const id = String(record.employee?.employeeId || '').toLowerCase();
        const dept = String(record.employee?.department || '').toLowerCase();
        if (!name.includes(term) && !id.includes(term) && !dept.includes(term)) return false;
      }
      if (filters.status && record.status !== filters.status) return false;
      // record.date is already a YYYY-MM-DD string, so these compare correctly.
      if (from && record.date < from) return false;
      if (to && record.date > to) return false;
      return true;
    });
  }, [attendanceRecords, filters]);

  const [form] = Form.useForm();
  const [shiftForm] = Form.useForm();

  // Fetch attendance records from API
  const fetchAttendanceRecords = useCallback(async () => {
    setLoading(true);
    try {
      const data = await attendanceService.getAll();
      // Transform API data to match our interface
      const transformedData = (data || []).map((record: any) => ({
        id: record.id,
        employeeId: record.employeeId,
        employee: record.employee ? {
          firstName: record.employee.firstName || '',
          lastName: record.employee.lastName || '',
          employeeId: record.employee.employeeId || record.employeeId,
          department: record.employee.department || 'N/A'
        } : {
          firstName: 'Unknown',
          lastName: '',
          employeeId: record.employeeId,
          department: 'N/A'
        },
        // These columns are `timestamp without time zone` — a wall-clock reading
        // from the attendance device, with no zone attached. Plain dayjs() reads
        // them as UTC and re-renders them in the browser's zone, which in Hong
        // Kong added 8 hours: a 23:39 punch displayed as 07:39 the next day.
        // The ZK Raw Data tab already uses dayjs.utc() for the same reason, so
        // the two screens now show the same clock reading.
        date: record.date ? dayjs.utc(record.date).format('YYYY-MM-DD') : '',
        checkIn: record.checkIn ? dayjs.utc(record.checkIn).format('HH:mm') : undefined,
        checkOut: record.checkOut ? dayjs.utc(record.checkOut).format('HH:mm') : undefined,
        breakTime: record.breakDuration || 0,
        hoursWorked: Number(record.totalHours) || 0,
        overtime: Number(record.overtimeHours) || 0,
        status: (record.status || 'present').toLowerCase(),
        location: record.location || 'office',
        isManualEntry: false,
        notes: record.notes
      }));
      setAttendanceRecords(transformedData);
    } catch (error: any) {
      console.error('Error fetching attendance records:', error);
      // Fall back to sample data if API fails
      setAttendanceRecords(sampleAttendanceRecords);
      message.warning('Using sample data - API connection failed');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch shifts from API
  const fetchShifts = useCallback(async () => {
    try {
      const data = await shiftService.getAll(true);
      const transformedData = (data || []).map((shift: any) => ({
        id: shift.id,
        name: shift.name,
        startTime: shift.startTime,
        endTime: shift.endTime,
        breakDuration: shift.breakDuration || 0,
        isActive: shift.isActive,
        departments: [] // API doesn't have departments yet
      }));
      setShifts(transformedData);
    } catch (error) {
      console.error('Error fetching shifts:', error);
      setShifts(sampleShifts);
    }
  }, []);

  // Fetch employees for dropdown
  const fetchEmployees = useCallback(async () => {
    try {
      const data = await employeeService.getAll();
      setEmployees(data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  }, []);

  // Sample data
  const sampleAttendanceRecords: AttendanceRecord[] = [
    {
      id: '1',
      employeeId: 'EMP001',
      employee: {
        firstName: 'John',
        lastName: 'Doe',
        employeeId: 'EMP001',
        department: 'Engineering'
      },
      date: '2024-06-26',
      checkIn: '09:00',
      checkOut: '18:00',
      breakTime: 60,
      hoursWorked: 8.0,
      overtime: 0,
      status: 'present',
      location: 'office',
      isManualEntry: false
    },
    {
      id: '2',
      employeeId: 'EMP002',
      employee: {
        firstName: 'Sarah',
        lastName: 'Johnson',
        employeeId: 'EMP002',
        department: 'Marketing'
      },
      date: '2024-06-26',
      checkIn: '09:15',
      checkOut: '17:45',
      breakTime: 45,
      hoursWorked: 7.75,
      overtime: 0,
      status: 'late',
      location: 'remote',
      isManualEntry: false,
      notes: 'Traffic delay'
    },
    {
      id: '3',
      employeeId: 'EMP003',
      employee: {
        firstName: 'Mike',
        lastName: 'Chen',
        employeeId: 'EMP003',
        department: 'Sales'
      },
      date: '2024-06-26',
      status: 'absent',
      hoursWorked: 0,
      overtime: 0,
      breakTime: 0,
      location: 'office',
      isManualEntry: true,
      notes: 'Sick leave'
    }
  ];

  const sampleShifts: Shift[] = [
    {
      id: '1',
      name: 'Day Shift',
      startTime: '09:00',
      endTime: '18:00',
      breakDuration: 60,
      isActive: true,
      departments: ['Engineering', 'Marketing', 'Sales']
    },
    {
      id: '2',
      name: 'Night Shift',
      startTime: '22:00',
      endTime: '06:00',
      breakDuration: 45,
      isActive: true,
      departments: ['Operations', 'Security']
    },
    {
      id: '3',
      name: 'Flexible',
      startTime: '10:00',
      endTime: '19:00',
      breakDuration: 60,
      isActive: true,
      departments: ['Engineering', 'Marketing']
    }
  ];

  const samplePolicies: AttendancePolicy[] = [
    {
      id: '1',
      name: 'Standard Policy',
      workingHours: 8,
      lateGracePeriod: 15,
      halfDayThreshold: 4,
      overtimeThreshold: 8,
      isActive: true
    },
    {
      id: '2',
      name: 'Flexible Policy',
      workingHours: 8,
      lateGracePeriod: 30,
      halfDayThreshold: 4,
      overtimeThreshold: 9,
      isActive: false
    }
  ];

  useEffect(() => {
    fetchAttendanceRecords();
    fetchShifts();
    fetchEmployees();
    setPolicies(samplePolicies);

    // Update current time every minute
    const timer = setInterval(() => {
      setCurrentTime(dayjs());
    }, 60000);

    return () => clearInterval(timer);
  }, [fetchAttendanceRecords, fetchShifts, fetchEmployees]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'present': return 'green';
      case 'late': return 'orange';
      case 'absent': return 'red';
      case 'half-day': return 'blue';
      case 'leave': return 'purple';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'present': return <CheckCircleOutlined />;
      case 'late': return <ExclamationCircleOutlined />;
      case 'absent': return <CloseCircleOutlined />;
      case 'half-day': return <ClockCircleOutlined />;
      case 'leave': return <CalendarOutlined />;
      default: return <ClockCircleOutlined />;
    }
  };

  const handleClockIn = async (employeeId: string) => {
    try {
      setLoading(true);
      await attendanceService.checkIn(employeeId);
      message.success('Clock in recorded successfully');
      fetchAttendanceRecords();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to record clock in');
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async (employeeId: string) => {
    try {
      setLoading(true);
      await attendanceService.checkOut(employeeId);
      message.success('Clock out recorded successfully');
      fetchAttendanceRecords();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to record clock out');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAttendance = async (values: any) => {
    try {
      setLoading(true);
      const recordData = {
        employeeId: values.employeeId,
        date: values.date.format('YYYY-MM-DD'),
        checkIn: values.checkIn ? values.date.format('YYYY-MM-DD') + 'T' + values.checkIn.format('HH:mm:ss') : undefined,
        checkOut: values.checkOut ? values.date.format('YYYY-MM-DD') + 'T' + values.checkOut.format('HH:mm:ss') : undefined,
        status: (values.status || 'present').toUpperCase(),
        notes: values.notes,
        location: values.location
      };

      if (editingAttendance) {
        // Update existing record
        await attendanceService.update(editingAttendance.id, recordData);
        message.success('Attendance record updated successfully');
      } else {
        // Add new record
        await attendanceService.create(recordData as any);
        message.success('Attendance record added successfully');
      }

      fetchAttendanceRecords();
      setIsAttendanceModalVisible(false);
      setEditingAttendance(null);
      form.resetFields();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to save attendance record');
    } finally {
      setLoading(false);
    }
  };

  const handleAddShift = async (values: any) => {
    try {
      setLoading(true);
      const shiftData = {
        name: values.name,
        code: values.name.toUpperCase().replace(/\s+/g, '_'),
        startTime: values.startTime.format('HH:mm'),
        endTime: values.endTime.format('HH:mm'),
        breakDuration: values.breakDuration || 0,
        isActive: true
      };

      if (editingShift) {
        // Update existing shift
        await shiftService.update(editingShift.id, shiftData);
        message.success('Shift updated successfully');
      } else {
        // Add new shift
        await shiftService.create(shiftData);
        message.success('Shift added successfully');
      }

      fetchShifts();
      setIsShiftModalVisible(false);
      setEditingShift(null);
      shiftForm.resetFields();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to save shift');
    } finally {
      setLoading(false);
    }
  };

  const handleEditAttendance = (record: AttendanceRecord) => {
    setEditingAttendance(record);
    form.setFieldsValue({
      employeeId: record.employeeId,
      date: dayjs(record.date),
      checkIn: record.checkIn ? dayjs(record.checkIn, 'HH:mm') : null,
      checkOut: record.checkOut ? dayjs(record.checkOut, 'HH:mm') : null,
      status: record.status,
      location: record.location,
      breakTime: record.breakTime,
      notes: record.notes
    });
    setIsAttendanceModalVisible(true);
  };

  const handleDeleteAttendance = (record: AttendanceRecord) => {
    Modal.confirm({
      title: 'Delete Attendance Record',
      content: `Are you sure you want to delete the attendance record for ${record.employee.firstName} ${record.employee.lastName} on ${record.date}?`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await attendanceService.delete(record.id);
          message.success('Attendance record deleted successfully');
          fetchAttendanceRecords();
        } catch (error: any) {
          message.error(error.response?.data?.error || 'Failed to delete attendance record');
        }
      }
    });
  };

  const handleEditShift = (shift: Shift) => {
    setEditingShift(shift);
    shiftForm.setFieldsValue({
      name: shift.name,
      startTime: dayjs(shift.startTime, 'HH:mm'),
      endTime: dayjs(shift.endTime, 'HH:mm'),
      breakDuration: shift.breakDuration,
      departments: shift.departments
    });
    setIsShiftModalVisible(true);
  };

  const handleDeleteShift = (shift: Shift) => {
    Modal.confirm({
      title: 'Delete Shift',
      content: `Are you sure you want to delete the shift "${shift.name}"?`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await shiftService.delete(shift.id);
          message.success('Shift deleted successfully');
          fetchShifts();
        } catch (error: any) {
          message.error(error.response?.data?.error || 'Failed to delete shift');
        }
      }
    });
  };

  const attendanceColumns: ColumnsType<AttendanceRecord> = [
    {
      title: 'Employee',
      key: 'employee',
      render: (_, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} />
          <div>
            <div>{`${record.employee.firstName} ${record.employee.lastName}`}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{record.employee.employeeId}</Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Department',
      dataIndex: ['employee', 'department'],
      key: 'department'
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => dayjs(date).format('MMM DD, YYYY')
    },
    {
      title: 'Check In',
      dataIndex: 'checkIn',
      key: 'checkIn',
      render: (time) => time || '-'
    },
    {
      title: 'Check Out',
      dataIndex: 'checkOut',
      key: 'checkOut',
      render: (time) => time || '-'
    },
    {
      title: 'Hours Worked',
      dataIndex: 'hoursWorked',
      key: 'hoursWorked',
      render: (hours) => `${hours.toFixed(2)}h`
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: (location) => <Tag>{location}</Tag>
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button type="text" icon={<EditOutlined />} onClick={() => handleEditAttendance(record)} />
          </Tooltip>
          <Tooltip title="Delete">
            <Button type="text" icon={<DeleteOutlined />} danger onClick={() => handleDeleteAttendance(record)} />
          </Tooltip>
        </Space>
      )
    }
  ];

  const shiftColumns: ColumnsType<Shift> = [
    {
      title: 'Shift Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Start Time',
      dataIndex: 'startTime',
      key: 'startTime'
    },
    {
      title: 'End Time',
      dataIndex: 'endTime',
      key: 'endTime'
    },
    {
      title: 'Break Duration',
      dataIndex: 'breakDuration',
      key: 'breakDuration',
      render: (duration) => `${duration} min`
    },
    {
      title: 'Departments',
      dataIndex: 'departments',
      key: 'departments',
      render: (departments) => (
        <div>
          {departments.map((dept: string) => (
            <Tag key={dept}>{dept}</Tag>
          ))}
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEditShift(record)} />
          <Button type="text" icon={<DeleteOutlined />} danger onClick={() => handleDeleteShift(record)} />
        </Space>
      )
    }
  ];

  const renderTimeClockCard = () => {
    const todayAttendance = attendanceRecords.filter(
      record => record.date === dayjs().format('YYYY-MM-DD')
    );
    const presentCount = todayAttendance.filter(record => record.status === 'present').length;
    const lateCount = todayAttendance.filter(record => record.status === 'late').length;
    const absentCount = todayAttendance.filter(record => record.status === 'absent').length;

    return (
      <Card title="Time Clock" style={{ marginBottom: 16 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={2}>{currentTime.format('HH:mm:ss')}</Title>
          <Text type="secondary">{currentTime.format('dddd, MMMM DD, YYYY')}</Text>
        </div>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Card size="small">
              <Statistic
                title="Present Today"
                value={presentCount}
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic
                title="Late Today"
                value={lateCount}
                valueStyle={{ color: '#faad14' }}
                prefix={<ExclamationCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic
                title="Absent Today"
                value={absentCount}
                valueStyle={{ color: '#ff4d4f' }}
                prefix={<CloseCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>

        <Space style={{ width: '100%', justifyContent: 'center' }}>
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            size="large"
            onClick={() => handleClockIn('current-user')}
            loading={loading}
          >
            Clock In
          </Button>
          <Button
            type="default"
            icon={<PauseCircleOutlined />}
            size="large"
            onClick={() => handleClockOut('current-user')}
            loading={loading}
          >
            Clock Out
          </Button>
        </Space>
      </Card>
    );
  };

  const renderAttendanceSummary = () => {
    const thisMonth = attendanceRecords.filter(record => 
      dayjs(record.date).month() === dayjs().month()
    );
    const totalDays = thisMonth.length;
    const presentDays = thisMonth.filter(record => record.status === 'present').length;
    const lateDays = thisMonth.filter(record => record.status === 'late').length;
    const absentDays = thisMonth.filter(record => record.status === 'absent').length;
    const attendanceRate = totalDays > 0 ? (presentDays / totalDays) * 100 : 0;

    return (
      <Card title="Monthly Summary" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Statistic title="Attendance Rate" value={attendanceRate} suffix="%" />
            <Progress percent={attendanceRate} strokeColor="#52c41a" />
          </Col>
          <Col span={12}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>Present Days: <Badge count={presentDays} color="green" /></div>
              <div>Late Days: <Badge count={lateDays} color="orange" /></div>
              <div>Absent Days: <Badge count={absentDays} color="red" /></div>
            </Space>
          </Col>
        </Row>
      </Card>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Title level={2}>Attendance Management</Title>
        <Space>
          <Button icon={<DownloadOutlined />}>Export Report</Button>
          <Button 
            type="primary" 
            icon={<ClockCircleOutlined />}
            onClick={() => setIsAttendanceModalVisible(true)}
          >
            Add Attendance
          </Button>
        </Space>
      </Row>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <Tabs.TabPane tab="Time Clock" key="time-clock">
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              {renderTimeClockCard()}
            </Col>
            <Col xs={24} lg={12}>
              {renderAttendanceSummary()}
            </Col>
          </Row>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Attendance Records" key="attendance">
          <Card>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={8}>
                <Input
                  placeholder="Search employees..."
                  prefix={<SearchOutlined />}
                  value={filters.employee}
                  onChange={(e) => setFilters({ ...filters, employee: e.target.value })}
                />
              </Col>
              <Col xs={24} sm={8}>
                <RangePicker
                  style={{ width: '100%' }}
                  value={filters.dateRange}
                  onChange={(range) =>
                    setFilters({ ...filters, dateRange: (range as [Dayjs, Dayjs] | null) })
                  }
                />
              </Col>
              <Col xs={24} sm={8}>
                <Select
                  placeholder="Status"
                  style={{ width: '100%' }}
                  value={filters.status}
                  onChange={(value) => setFilters({ ...filters, status: value })}
                  allowClear
                >
                  <Option value="present">Present</Option>
                  <Option value="late">Late</Option>
                  <Option value="absent">Absent</Option>
                  <Option value="half-day">Half Day</Option>
                  <Option value="leave">Leave</Option>
                </Select>
              </Col>
            </Row>

            <Table
              columns={attendanceColumns}
              dataSource={filteredAttendanceRecords}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Shifts" key="shifts">
          <Card
            title="Shift Management"
            extra={
              <Button 
                type="primary" 
                icon={<ClockCircleOutlined />}
                onClick={() => setIsShiftModalVisible(true)}
              >
                Add Shift
              </Button>
            }
          >
            <Table
              columns={shiftColumns}
              dataSource={shifts}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Policies" key="policies">
          <Card title="Attendance Policies">
            <Row gutter={[16, 16]}>
              {policies.map(policy => (
                <Col xs={24} md={12} key={policy.id}>
                  <Card size="small" title={policy.name}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>Working Hours: {policy.workingHours}h</div>
                      <div>Late Grace Period: {policy.lateGracePeriod} min</div>
                      <div>Half Day Threshold: {policy.halfDayThreshold}h</div>
                      <div>Overtime Threshold: {policy.overtimeThreshold}h</div>
                      <Tag color={policy.isActive ? 'green' : 'red'}>
                        {policy.isActive ? 'Active' : 'Inactive'}
                      </Tag>
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Tabs.TabPane>

        <Tabs.TabPane
          tab={
            <span>
              <DesktopOutlined />
              ZK Devices
            </span>
          }
          key="zk-devices"
        >
          <ZKDeviceManagement />
        </Tabs.TabPane>
      </Tabs>

      {/* Add/Edit Attendance Modal */}
      <Modal
        title={editingAttendance ? 'Edit Attendance Record' : 'Add Attendance Record'}
        open={isAttendanceModalVisible}
        onCancel={() => {
          setIsAttendanceModalVisible(false);
          setEditingAttendance(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleAddAttendance}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="employeeId" label="Employee" rules={[{ required: true }]}>
                <Select
                  placeholder="Select employee"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {employees.map(emp => (
                    <Option key={emp.id} value={emp.id}>
                      {`${emp.firstName} ${emp.lastName} (${emp.employeeId})`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="date" label="Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="checkIn" label="Check In Time">
                <TimePicker style={{ width: '100%' }} format="HH:mm" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="checkOut" label="Check Out Time">
                <TimePicker style={{ width: '100%' }} format="HH:mm" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                <Select>
                  <Option value="present">Present</Option>
                  <Option value="late">Late</Option>
                  <Option value="absent">Absent</Option>
                  <Option value="half-day">Half Day</Option>
                  <Option value="leave">Leave</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="location" label="Location">
                <Select>
                  <Option value="office">Office</Option>
                  <Option value="remote">Remote</Option>
                  <Option value="field">Field</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="breakTime" label="Break Time (minutes)">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingAttendance ? 'Update Attendance' : 'Add Attendance'}
              </Button>
              <Button onClick={() => {
                setIsAttendanceModalVisible(false);
                setEditingAttendance(null);
                form.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add/Edit Shift Modal */}
      <Modal
        title={editingShift ? 'Edit Shift' : 'Add New Shift'}
        open={isShiftModalVisible}
        onCancel={() => {
          setIsShiftModalVisible(false);
          setEditingShift(null);
          shiftForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form form={shiftForm} layout="vertical" onFinish={handleAddShift}>
          <Form.Item name="name" label="Shift Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="startTime" label="Start Time" rules={[{ required: true }]}>
                <TimePicker style={{ width: '100%' }} format="HH:mm" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="endTime" label="End Time" rules={[{ required: true }]}>
                <TimePicker style={{ width: '100%' }} format="HH:mm" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="breakDuration" label="Break Duration (minutes)">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="departments" label="Applicable Departments" rules={[{ required: true }]}>
            <Select mode="multiple" placeholder="Select departments">
              <Option value="Engineering">Engineering</Option>
              <Option value="Marketing">Marketing</Option>
              <Option value="Sales">Sales</Option>
              <Option value="HR">HR</Option>
              <Option value="Operations">Operations</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingShift ? 'Update Shift' : 'Add Shift'}
              </Button>
              <Button onClick={() => {
                setIsShiftModalVisible(false);
                setEditingShift(null);
                shiftForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AttendanceManagement; 